import React, { useEffect, useState } from 'react';
import { Card, Table, Typography, Space, Button } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import { useAuth } from '../context/AuthContext';
import { listGroups, listUsers } from '../services/api';
import { useThemeStyles } from '../context/ThemeContext';
import PageHeader from '../components/PageHeader';
import { pageShellStyle } from '../theme';

const { Text } = Typography;

function MyGroups() {
  const { token, user, isAdmin, isSuperAdmin } = useAuth();
  const themeStyles = useThemeStyles();
  const [groups, setGroups] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);

  /** Same as sidebar: platform `admin` role cannot access; `super_admin` can. */
  const canAccessMyGroups = isAdmin && user?.role !== 'admin';

  const fetchGroups = async () => {
    if (!token || !canAccessMyGroups) return;
    setLoading(true);
    try {
      const res = await listGroups(token);
      const allGroups = Array.isArray(res.data) ? res.data : [];
      if (isSuperAdmin) {
        setGroups(allGroups);
      } else {
        const myId = user?.user_id;
        setGroups(allGroups.filter((g) => (g.admin_ids || []).includes(myId)));
      }
    } catch (e) {
      // Best-effort; keep existing list
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    if (!token) return;
    try {
      const res = await listUsers(token);
      setUsers(Array.isArray(res.data) ? res.data : []);
    } catch (_) {
      setUsers([]);
    }
  };

  useEffect(() => {
    fetchGroups();
  }, [token, canAccessMyGroups, isSuperAdmin]);

  useEffect(() => {
    if (token && canAccessMyGroups) fetchUsers();
  }, [token, canAccessMyGroups]);

  if (!canAccessMyGroups) {
    return (
      <Card style={{ margin: 24, background: themeStyles.cardBg }}>
        <Text style={{ color: themeStyles.textSecondary }}>
          {user?.role === 'admin'
            ? 'My Groups is not available for administrator accounts.'
            : 'Super admin access required.'}
        </Text>
      </Card>
    );
  }

  const columns = [
    { title: 'Name', dataIndex: 'name', key: 'name', render: (t) => <Text strong>{t}</Text> },
    { title: 'Description', dataIndex: 'description', key: 'description' },
    {
      title: 'Admin',
      key: 'group_admin',
      render: (_, record) => {
        const adminIds = record.admin_ids || [];
        const names = adminIds.map((uid) => users.find((u) => u._id === uid)?.username ?? uid);
        return names.length ? names.join(', ') : '—';
      },
    },
  ];

  return (
    <div style={pageShellStyle}>
      <Card style={{ background: themeStyles.cardBg }}>
        <PageHeader
          title="My Groups"
          actions={(
            <Button icon={<ReloadOutlined />} onClick={fetchGroups} loading={loading}>
              Refresh
            </Button>
          )}
        />
        <Table dataSource={groups} columns={columns} rowKey="_id" loading={loading} pagination={{ pageSize: 10, hideOnSinglePage: true }} tableLayout="fixed" scroll={{ x: 'max-content' }} />
      </Card>
    </div>
  );
}

export default MyGroups;

